import { AddIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { Box, Button, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { OverlayRouter } from "../components/OverlayRouter";
import { useMedia } from "../components/ui-elements/SizeDetector";
import {
  ISubjectStore,
  ITutorSubject,
  ISupportedCountryStore,
} from "../stores";
import { useOverlayDisclosureWithCrisp } from "../utils/hooks";
import subjectData from "./formData/subject.json";
import {
  ConfirmationPrompt,
  CustomPopupDisplay,
  displayToast,
} from "./forms/SharedComponents";
import SubjectAddition from "./SubjectComponents";
import { EditSubjectWrapper } from "./SubjectEditView";
import { SubjectPrompt } from "./SubjectPrompt";
// import { ConfirmationPrompt } from "./SubjectEditForm";

const CreateSubjectWrapper: React.FC<{
  store: ISubjectStore;
  triggerWelcomeModal?: boolean;
  renderPreview: any;
  educationAndWorkHistoryData?: any;
  getPriceSuggestion?: any;
  onSubjectAction: ({
    action,
    subject,
    subjects,
    status,
  }: {
    action: "edit" | "take_test" | "archive" | "delete" | "add" | "start";
    subject?: ITutorSubject;
    subjects?: Array<ITutorSubject>;
    status?: string;
  }) => any;
  canEditSubject?: boolean;
  proceedToNextStep?: React.ReactNode;
  children?: React.ReactNode;
}> = observer(
  ({
    store,
    children,
    triggerWelcomeModal,
    getPriceSuggestion,
    onSubjectAction,
    proceedToNextStep,
  }) => {
    const isMobile = useMedia("(max-width: 420px)");
    const incompleteSubjects = store.tutorSubjects.filter((subject) =>
      ["not-started", "in-progress"].includes(subject.status)
    );
    return (
      <>
        <SubjectPrompt
          incompleteSubjects={incompleteSubjects}
          cantAddMoreSubjects={!store.canAddMoreSubjects}
        />

        <Box
          id="subject-selection"
          width="100%"
          borderWidth={{ md: "2xs" }}
          borderColor={{ md: "neutral.500" }}
          p={{ md: 8 }}
          boxShadow={{ md: "sm" }}
          rounded="lg"
        >
          <Stack mb={4}>
            <Text fontWeight="bold" fontSize={["3xl", "4xl"]}>
              {subjectData.subjectsPage.header}
            </Text>
            <Text color="gray.700">{subjectData.subjectsPage.subHeader}</Text>
          </Stack>

          <Text mt={6} fontWeight="semibold" color="gray.700">
            {subjectData.emptySubjectSection.emptySubjectInfo}
          </Text>

          <Button
            mt={8}
            leftIcon={<AddIcon />}
            variant="outline"
            colorScheme="blue"
            onClick={() => {
              onSubjectAction({ action: "add" });
            }}
          >
            {subjectData.emptySubjectSection.emptySubjectButton}
          </Button>

          {children}
          {/* {proceedToNextStep} */}
        </Box>
      </>
    );
  }
);

const TutorSubjectsPage: React.FC<{
  store: ISubjectStore;
  onTakeTest?: (subject: any) => string;
  showWelcomeModal?: boolean;
  label?: string;
  buttonText?: string;
  supportedCountry?: ISupportedCountryStore;
  buttonIsDisabled?: boolean;
  displayType?: string;
  isLiteTutor?: boolean;
  // currentStep: string;
  getPriceSuggestion?: any;
  renderPreview?: any;
  isCollapsed?: boolean;
  onEditSubject?: (subject: any) => string;
  educationAndWorkHistoryData?: any;
  canEditSubject?: boolean;
  toggleNavigation?: (value: boolean) => void;
  proceedToNextStep?: React.ReactNode;
  onSubmitSubjects: () => any;
  generateDescription: (p: {
    name: string;
    category: string;
    subcategory: string;
  }) => any;
}> = observer(
  ({
    store,
    onTakeTest,
    showWelcomeModal,
    renderPreview,
    educationAndWorkHistoryData,
    onEditSubject,
    isLiteTutor,
    getPriceSuggestion,
    canEditSubject = true,
    onSubmitSubjects,
    toggleNavigation,
    proceedToNextStep,
    generateDescription,
    // currentStep,
  }) => {
    const [path, setPath] = React.useState("/select-subject");
    const { isOpen, onClose, onOpen } = useOverlayDisclosureWithCrisp(path);
    const [deletePrompt, setDeletePrompt] = React.useState(false);
    const [canDelete, setCanDelete] = React.useState(true);
    const [openWelcomeModal, displayWelcomeModal] =
      React.useState(showWelcomeModal);

    const successStatus = subjectData.subjectsPage.toastData
      .successStatus as "success";

    async function onSubjectAction({
      action,
      subject,
      subjects,
      status,
    }: {
      action: "edit" | "take_test" | "archive" | "delete" | "add" | "start";
      subject?: ITutorSubject;
      subjects?: Array<ITutorSubject>;
      status?: string;
    }) {
      if (action === "add") {
        // if (store.tutorSubjects.length === 0) {
        //   setPath("/subject-selection-empty");
        //   onOpen();
        // } else {
        setPath("/select-subject");
        console.log("path", path);
        onOpen();
        console.log(window.location.hash);
        // }
      } else if (action === "edit") {
        if (subject) {
          store.saveSubject(subject.id);
          store.setCurrentSubjectId(subject.id);
          // return onEditSubject(subject.id);
          // setOpenEdit(true);
        }
      } else if (action === "take_test") {
        if (subject) {
          store.setCurrentSubjectId(subject.id);
        }
        // onTakeTest(subject.name);
      } else if (action === "delete") {
        if (subjects) {
          let _delete =
            status === "not-started" ||
            subjects[0].title === "" ||
            subjects[0].description === "";
          setCanDelete(_delete);
          // store.setCurrentSubjectId(subject.id);
          await store.deleteBulkSubjects(subjects, status || "");
          displayToast({
            status: successStatus,
            title: canDelete
              ? subjectData.subjectsPage.toastData.subjectDeletedTitle
              : subjectData.subjectsPage.toastData.subjectArchiveTitle,
            duration: 4000,
            position: "top",
            description: canDelete
              ? subjectData.subjectsPage.toastData.subjectDeletedDescription
              : subjectData.subjectsPage.toastData.subjectArchiveDescription,
          });
          // setDeletePrompt(true);
        }
      } else if (action === "start") {
        displayWelcomeModal(false);
      }
    }
    async function submitAllSubjects() {
      if (store.totalSelectedSubjects.length === 0) {
        store.setError("You must select at least one subject");
      } else if (
        store.totalSelectedSubjects.filter(
          (i: any) => !["active", "pending"].includes(i.status)
        ).length > 10
      ) {
        store.setError("You can only select maximum of 10 subjects");
      } else {
        try {
          await onSubmitSubjects();
          onClose();
          displayToast({
            status: successStatus,
            title: subjectData.subjectsPage.toastData.subjectAddedTitle,
            duration: 4000,
            position: "top",
            description:
              subjectData.subjectsPage.toastData.subjectAddedDescription,
          });
        } catch (error) {
          console.log(error);
          store.setError("Error saving subject");
        }
      }
    }
    // const Component =
    //   store.tutorSubjects.length === 0
    //     ? CreateSubjectWrapper
    //     : EditSubjectWrapper;

    // const Component = EditSubjectWrapper;

    return (
      <OverlayRouter>
        <EditSubjectWrapper
          generateDescription={generateDescription}
          renderPreview={renderPreview}
          store={store}
          isLiteTutor={isLiteTutor}
          canAddSubject={!isLiteTutor}
          toggleNavigation={toggleNavigation}
          triggerWelcomeModal={openWelcomeModal}
          onSubjectAction={onSubjectAction}
          educationAndWorkHistoryData={educationAndWorkHistoryData}
          getPriceSuggestion={getPriceSuggestion}
          canEditSubject={canEditSubject}
          proceedToNextStep={proceedToNextStep}
        >
          {/* delete subject modal action */}
          <ConfirmationPrompt
            isOpen={deletePrompt}
            canDelete={canDelete}
            onClose={() => setDeletePrompt(false)}
            onDelete={async () => {
              await store.deleteSubject();
              setDeletePrompt(false);
              displayToast({
                status: successStatus,
                title: canDelete
                  ? subjectData.subjectsPage.toastData.subjectDeletedTitle
                  : subjectData.subjectsPage.toastData.subjectArchiveTitle,
                duration: 4000,
                position: "top",
                description: canDelete
                  ? subjectData.subjectsPage.toastData.subjectDeletedDescription
                  : subjectData.subjectsPage.toastData
                      .subjectArchiveDescription,
              });
            }}
            loading={store.loading}
          />

          {/* Subject creation drawer */}
          <AddSubjectDrawer
            onSubmit={submitAllSubjects}
            store={store}
            isOpen={isOpen}
            onClose={onClose}
          />
        </EditSubjectWrapper>
      </OverlayRouter>
    );
  }
);
export const AddSubjectDrawer: React.FC<{
  store: ISubjectStore;
  isOpen: boolean;
  onClose: any;
  onSubmit: any;
}> = observer(({ store, isOpen, onClose, onSubmit }) => {
  const successStatus = subjectData.subjectsPage.toastData
    .successStatus as "success";

  return (
    <CustomPopupDisplay
      path={
        // store.tutorSubjects.length === 0
        //   ? "/subject-selection-empty"
        "/select-subject"
      }
      isOpen={isOpen}
      isDesktopDrawer={true}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <SubjectAddition
        onSubmit={onSubmit}
        loading={store.loading}
        store={store}
        onBackClick={() => {
          onClose();
        }}
      />
    </CustomPopupDisplay>
  );
});
export default TutorSubjectsPage;
